// ITEM 9 — HER IT TICKET SHEET, grouped by her own Pacific day.
//
// Hookless except the one piece of state a copy box needs, so a gate CALLS it: her three states —
// a read that did not land, a genuinely empty sheet, and days with tickets in them — are what this
// surface has to get right (R64's four-way ruling one panel over).
//
// **ITEM 4b CLAUSE 1 (2026-08-13): THE ROW CARRIES ITS NUMBER.** Her clause is *"sequential, and
// visible EVERYWHERE the ticket appears... ONE number aligning all surfaces so she can reconcile at
// a glance."* The number is `it_tickets.id` and it is written by `ticketRef` — imported rather than
// formatted here, because a `#` typed at a surface is a second spelling of her one number and this
// surface is the one she reconciles the copy box against (R28/R29).
//
// **ITEM 4b CLAUSE 2 (2026-08-13): THE ROW OPENS ITS CONVERSATION.** Her clause is *"on the IT sheet
// each ticket row gains a VIEW CONVERSATION control beside Copy for CC and Mark cleared, opening the
// full transcript... NOTHING THE HOUSE SAYS TO A MEMBER IS INVISIBLE TO THE OWNER."* Migration 106
// wrote the link, `fileTicket` fills it and the desk's own list already joins on it — **and this
// surface, the one her clause names, had no control and its payload did not even carry the id.**
// A capability wired to nothing has no error state (R38/R165), and here the absence read as a
// conversation that was simply not reachable from a ticket.
import React, { useCallback, useEffect, useState } from "react";
import { Copy, Check, MessageSquare } from "lucide-react";
import { ticketRef } from "../itTickets.js";

export type Ticket = {
  id: string; email: string; issue: string; fix: string;
  source: string; dayKey: string; date: string; clearedAt: string | null;
  // The conversation this ticket was filed about. `null` for the Lala doors and the patrol rows,
  // which were never filed about a written-in message (R18: absent is an answer, not a gap).
  contactId?: string | null;
};
export type TicketDay = { dayKey: string; label: string; tickets: Ticket[]; cleared: boolean; copy: string };

export function OwnerTicketDay({
  day, onClear, onCopy, onOpenConversation, copied, busy,
}: {
  day: TicketDay;
  onClear: (dayKey: string) => void;
  onCopy: (day: TicketDay) => void;
  onOpenConversation: (contactId: string) => void;
  copied: boolean;
  busy: boolean;
}) {
  return (
    <section className={day.cleared ? "it-day is-cleared" : "it-day"}>
      <header className="it-day__head">
        <h3 className="it-day__label">{day.label || day.dayKey}</h3>
        <span className="it-day__count">{day.tickets.length} {day.tickets.length === 1 ? "ticket" : "tickets"}</span>
        {day.cleared ? <span className="it-day__done">Cleared</span> : null}
        <div className="it-day__acts">
          <button type="button" className="it-copy" onClick={() => onCopy(day)}
            aria-label={`Copy the work order for ${day.label || day.dayKey}`}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy for CC"}
          </button>
          {day.cleared ? null : (
            <button type="button" className="it-clear" disabled={busy}
              onClick={() => onClear(day.dayKey)}>Mark cleared</button>
          )}
        </div>
      </header>
      {/* >>> **HER REPORT, 2026-08-14: *THE OWNER CANNOT LOCATE THE CONVERSATIONS.*** <<<

          **MEASURED BEFORE ANYTHING WAS MOVED (R25/R180): 90 tickets on this sheet and exactly ONE
          carries a conversation.** 85 are patrol rows and 3 came from a Lala turn — none of those was
          ever filed about a written-in message, so none of them HAS a transcript. The control was
          neither undeployed nor gated (it is in the live bundle, verified on the deployed asset): it
          renders on one row in ninety, which is indistinguishable from missing.

          So the row that HAS one reads as a door — `is-openable` marks the whole row rather than
          leaving a 13px button at the end of it. **The other 89 are unchanged and draw nothing**,
          because her own ITEM A clause is that a control she can see is one she can act on, and a
          door onto a conversation that does not exist is the dead box that clause forbids. */}
      <ul className="it-list">
        {day.tickets.map((t) => (
          <li key={t.id} className={`${t.clearedAt ? "it-row is-cleared" : "it-row"}${t.contactId ? " is-openable" : ""}`}>
            {/* A row with no number draws none rather than an invented one (R141) — `ticketRef`
                answers "" and the span is not rendered at all, so nothing empty is labelled. */}
            {ticketRef(t.id) ? <span className="it-row__ref">{ticketRef(t.id)}</span> : null}
            <span className="it-row__email">{t.email}</span>
            <span className="it-row__issue">{t.issue}</span>
            {t.fix ? <span className="it-row__fix">Fix: {t.fix}</span> : null}
            {/* **HER CLAUSE 2's DOOR, AND IT IS DRAWN ONLY WHERE THERE IS SOMETHING BEHIND IT.** A
                ticket filed from a Lala turn or by the patrol was never filed about a written-in
                message, so there is no transcript to open — and her own ITEM A clause is that a
                control she can see is one she can act on, *or it does not render*. Drawing it
                unconditionally would put a dead door on the majority of rows on this sheet. */}
            {t.contactId ? (
              <button type="button" className="it-row__open"
                onClick={() => onOpenConversation(String(t.contactId))}
                aria-label={`View the conversation behind ticket ${ticketRef(t.id) || t.id}`}>
                <MessageSquare size={13} /> View conversation
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function OwnerTickets({
  days, onClear, onCopy, onOpenConversation, copiedDay, busy, note,
}: {
  // `null` is the third state and NEVER `[]` (R18/R64): a sheet that could not be read must not be
  // drawn as a house with nothing broken in it.
  days: TicketDay[] | null;
  onClear: (dayKey: string) => void;
  onCopy: (day: TicketDay) => void;
  onOpenConversation: (contactId: string) => void;
  copiedDay: string;
  busy: boolean;
  note: string;
}) {
  return (
    <div className="it-sheet">
      {note ? <p className="owner-note">{note}</p> : null}
      {days === null ? <p className="owner-empty">Reading the sheet...</p>
        : days.length === 0 ? <p className="owner-empty">Nothing has been reported. Quiet is good.</p>
        : days.map((d) => (
          <OwnerTicketDay key={d.dayKey} day={d} onClear={onClear} onCopy={onCopy}
            onOpenConversation={onOpenConversation}
            copied={copiedDay === d.dayKey} busy={busy} />
        ))}
    </div>
  );
}

/**
 * The stateful shell. It holds the fetch and the copy state; everything it decides is above, so the
 * gate calls the panel with fabricated days rather than needing a browser (R21/R140's split).
 */
export function OwnerTicketsTab({ onDenied, onOpenConversation }: {
  onDenied: () => void;
  onOpenConversation: (contactId: string) => void;
}) {
  const [days, setDays] = useState<TicketDay[] | null>(null);
  const [note, setNote] = useState("");
  const [copiedDay, setCopiedDay] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/owner/tickets", { credentials: "same-origin" });
      if (res.status === 403 || res.status === 401) { onDenied(); return; }
      const data = await res.json().catch(() => null);
      // R53/R64: the ANSWER is read, never the status code, and a refusal keeps the last good sheet
      // rather than blanking it. `days` is only replaced when the server really sent one.
      if (data?.ok && Array.isArray(data.days)) { setDays(data.days); setNote(""); }
      else setNote(String(data?.message || "The ticket sheet could not be read right now."));
    } catch {
      setNote("The ticket sheet could not be read right now.");
    }
  }, [onDenied]);

  useEffect(() => { void load(); }, [load]);

  const copy = useCallback(async (day: TicketDay) => {
    // R56: the flash IS the only thing this control says, so it carries the ANSWER — a refused write
    // never wears "Copied".
    try {
      await navigator.clipboard.writeText(day.copy);
      setCopiedDay(day.dayKey);
      setTimeout(() => setCopiedDay(""), 2200);
    } catch {
      setNote("This browser would not let me copy. Select the text and copy it by hand.");
    }
  }, []);

  const clear = useCallback(async (dayKey: string) => {
    setBusy(true);
    try {
      const res = await fetch("/api/owner/tickets/clear", {
        method: "POST", credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dayKey }),
      });
      const data = await res.json().catch(() => null);
      if (data?.ok) await load();
      else setNote(String(data?.message || "That day could not be cleared right now."));
    } catch {
      setNote("That day could not be cleared right now.");
    } finally { setBusy(false); }
  }, [load]);

  return <OwnerTickets days={days} onClear={clear} onCopy={copy} onOpenConversation={onOpenConversation}
    copiedDay={copiedDay} busy={busy} note={note} />;
}
